import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from '@mui/material'
import { useState } from 'preact/hooks'
import type { QuestionFields } from '../api/generated/QuestionFields'
import type { QuestionKind } from '../api/generated/QuestionKind'
import { useAction } from '../api/useAction'
import { dropsAnswers, isQuestionKind, kindLabels, parseOptions } from './form'

const inputValue = (e: Event) => (e.target as HTMLInputElement).value

const messages = {
  invalid_question:
    'Question invalide : libellé de 200 caractères au plus, et 2 à 50 choix différents pour un choix unique.',
}

export function QuestionDialog({
  title,
  initial,
  onSubmit,
  onClose,
}: {
  title: string
  /** Set when editing a question that may have answers. */
  initial?: QuestionFields
  onSubmit: (fields: QuestionFields) => Promise<void>
  onClose: () => void
}) {
  const [label, setLabel] = useState(initial?.label ?? '')
  const [kind, setKind] = useState<QuestionKind>(initial?.kind ?? 'short_text')
  const [options, setOptions] = useState(initial?.options.join('\n') ?? '')
  const [required, setRequired] = useState(initial?.required ?? false)
  const { error, pending, run } = useAction(messages)
  const submit = (e: Event) => {
    e.preventDefault()
    run(() => onSubmit({ label, kind, options: parseOptions(options), required }))
  }
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <form onSubmit={submit}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Question"
              required
              autoFocus
              value={label}
              onInput={(e) => setLabel(inputValue(e))}
              slotProps={{ htmlInput: { maxLength: 200 } }}
            />
            <TextField
              select
              label="Type de réponse"
              value={kind}
              onChange={(e) => {
                const value = inputValue(e)
                if (isQuestionKind(value)) setKind(value)
              }}
              slotProps={{ select: { native: true } }}
            >
              {Object.entries(kindLabels).map(([value, text]) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </TextField>
            {initial && dropsAnswers(initial.kind, kind) && (
              <Alert severity="warning">Changer de type efface les réponses déjà données.</Alert>
            )}
            {kind === 'single_choice' && (
              <TextField
                label="Choix, un par ligne"
                required
                multiline
                minRows={3}
                value={options}
                onInput={(e) => setOptions(inputValue(e))}
                helperText={
                  initial
                    ? 'Les réponses déjà données restent, même pour un choix retiré.'
                    : undefined
                }
              />
            )}
            <FormControlLabel
              control={
                <Switch checked={required} onChange={(_, checked) => setRequired(checked)} />
              }
              label="Réponse obligatoire"
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Annuler</Button>
          <Button type="submit" variant="contained" loading={pending}>
            Enregistrer
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
